// The "Wi-Fi printing" setup dialog: network, print server and printer key, then the device is switched to Wi-Fi
// printing. The logic is in wifisetup.js; the device commands in device.js.
import { deriveFromKey, generateKey, secretOf } from '../server/crypto.js';
import { addPrinter } from '../server/keystore.js';
import { applyWifiSetup, WIFI_SETUP_STEPS } from './wifisetup.js';
import { networkForm } from './wifinetwork_ui.js';
import { button, openWizardDialog, paragraph } from './wizard_dialog.js';

const field = (type, placeholder, id) => Object.assign(document.createElement('input'), { type, placeholder, id, autocomplete: 'off', spellcheck: false });

// getDevice(): the current DeviceLink; reconnect(): waits for the restarted device. keyStore: the printers this browser
// knows. onDone(rec): the printer the device now pushes to (a key store record).
export function openWifiSetup({ getDevice, reconnect, keyStore, onDone }) {
  const state = { ssid: '', password: '', keepWifi: false, printerId: 'new', label: '', serverUrl: location.origin };
  const dlg = openWizardDialog({ onRequestClose: () => dlg.close() });
  const show = dlg.show;

  const intro = () => {
    const next = button('Next', network);
    show(
      'Wi-Fi printing',
      [
        paragraph('The device sends what the C64 prints, encrypted, to a print server over Wi-Fi, and you read it here from anywhere. You need the network name and password (2.4&nbsp;GHz), the address of the print server (this page&rsquo;s server by default) and a printer key.'),
        paragraph('The Wi-Fi radio and the bus never work at the same time: the device only switches the radio on for a moment to send, and holds the bus meanwhile.'),
      ],
      [button('Cancel', () => dlg.close(), 'outline secondary'), next],
    );
  };

  const network = async () => {
    // A device that already has a network (from an earlier setup, or the bus pin wizard) can keep it: changing only
    // the print server or the printer should not mean typing the password again.
    let stored = false;
    try {
      stored = !!(await getDevice().netStatus())?.hasCredentials;
    } catch {
      // unknown: show the form only
    }
    const form = networkForm(getDevice, state);
    const next = button('Next', () => {
      const v = form.values();
      if (!v) return;
      state.ssid = v.ssid;
      state.password = v.password;
      state.keepWifi = false;
      printer();
    });
    const keep = button('Keep Wi-Fi as is', () => {
      state.keepWifi = true;
      printer();
    }, 'secondary');
    show(
      'Wi-Fi network',
      [...(stored ? [paragraph('The device already has a Wi-Fi network stored. Keep it, or enter a new one below.')] : []), ...form.nodes],
      [button('Back', intro, 'outline secondary'), ...(stored ? [keep] : []), next],
    );
  };

  const printer = async () => {
    const recs = (await keyStore.loadAll()).filter((r) => r.kind === 'full').sort((a, b) => a.added - b.added);
    const url = field('text', 'https://print.example.org', 'wifi-server');
    url.value = state.serverUrl;
    const name = field('text', 'Name for a new printer (optional)', 'wifi-label');
    name.value = state.label;
    const choices = [{ id: 'new', text: 'A new printer (a new key is made in this browser)' }, ...recs.map((r) => ({ id: r.id, text: `The printer “${r.name || r.id.slice(0, 6)}” (its key is in this browser)` }))];
    const group = document.createElement('div');
    for (const c of choices) {
      const radio = Object.assign(document.createElement('input'), { type: 'radio', name: 'wifi-printer', value: c.id, checked: c.id === state.printerId });
      const label = document.createElement('label');
      label.append(radio, ` ${c.text}`);
      radio.onchange = () => (state.printerId = c.id);
      group.append(label);
    }
    const next = button('Set up the device', () => {
      state.serverUrl = url.value.trim();
      state.label = name.value.trim();
      run(recs);
    });
    show(
      'Print server and printer',
      [paragraph('Which printer should this device print to? A key that is already in this browser lets the device take over that printer (for example a replacement device).'), group, name, paragraph('Address of the print server:'), url],
      [button('Back', network, 'outline secondary'), next],
    );
  };

  const run = async (recs) => {
    // The whole list from the start: pending (circle), working (spinner), done (check mark), failed (cross).
    const lines = document.createElement('ul');
    lines.className = 'steps';
    const items = WIFI_SETUP_STEPS.map((text, i) => {
      const li = document.createElement('li');
      li.dataset.state = 'pending';
      li.textContent = i === 0 && state.keepWifi ? 'Keeping the Wi-Fi network as it is' : text;
      lines.append(li);
      return li;
    });
    const mark = (current) => items.forEach((li, i) => (li.dataset.state = i < current ? 'done' : i === current ? 'working' : 'pending'));
    let current = -1;
    show('Setting up…', [paragraph('Do not unplug the device.'), lines], []);
    try {
      const existing = recs.find((r) => r.id === state.printerId);
      const secret = existing ? existing.secret : generateKey();
      const keys = await deriveFromKey(secret);
      await applyWifiSetup({
        getDevice,
        reconnect,
        ssid: state.ssid,
        password: state.password,
        keepWifi: state.keepWifi,
        serverUrl: state.serverUrl,
        key: secret,
        onStep: (t) => {
          current = WIFI_SETUP_STEPS.indexOf(t);
          mark(current);
        },
      });
      mark(items.length);
      const rec = existing ?? (await addPrinter(keyStore, { kind: 'full', secret: secretOf(keys) }, keys.id, state.label));
      show(
        'Done',
        [paragraph('The device is connected to the print server and ready. Prints now go there: look at them under <strong>Showing</strong>. Keep the login link of the printer (Wi-Fi printers…, Login link): it is how you get at the prints from another browser.')],
        [button('Close', () => dlg.close(), 'outline secondary'), button('Show this printer', () => { onDone(rec); dlg.close(); })],
      );
    } catch (e) {
      if (items[current]) items[current].dataset.state = 'failed';
      show('That did not work', [paragraph(e.message), lines], [button('Close', () => dlg.close(), 'outline secondary'), button('Try again', network)]);
    }
  };

  intro();
}
